'use client'

import { User } from '@/lib/api/common/interfaces/user.interface'
import { getAccessibleUsers, getUsersByCompany } from '@/lib/api/users/users-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdClose, MdPerson, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (userId: number | null, user: User | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    /**
     * Escopo da busca. Sem isto, vale a unidade ativa do usuário (o `CompanySelector` do
     * topo) — o default histórico. Telas que exibem dados de outra filial ou da organização
     * inteira **precisam** informar o escopo, senão as opções ficam menores que os dados na
     * tela e um registro visível fica impossível de filtrar. Ver a aba de relatórios.
     */
    scope?: 'company' | 'organization'
    /** Empresa da busca quando `scope` é `company`. Default: a unidade ativa. */
    companyId?: number
    /**
     * `sm` deixa o campo na altura dos demais controles de uma barra de filtros
     * (`input-sm`), como no `ClientSelector`/`PartnerSelector`. No `sm` o usuário
     * escolhido também vira UMA linha no estilo do input — o bloco de duas linhas
     * (nome + e-mail) do `md` desalinharia a barra. Default `md` (formulários).
     */
    size?: 'sm' | 'md'
}

export function UserSelector({
    value,
    onChange,
    label = 'Usuário',
    placeholder = 'Buscar usuário...',
    disabled = false,
    className = '',
    scope = 'company',
    companyId: companyIdProp,
    size = 'md',
}: Props) {
    const { user } = useAuth()
    const companyId = companyIdProp ?? user?.companyId
    const [users, setUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selected, setSelected] = useState<User | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const isOrgScope = scope === 'organization'

    const load = async () => {
        if (!isOrgScope && !companyId) return
        setLoading(true)
        try {
            const res = isOrgScope
                ? await getAccessibleUsers()
                : await getUsersByCompany(companyId!)
            if (res.success && Array.isArray(res.result)) setUsers(res.result)
        } finally {
            setLoading(false)
        }
    }

    // Trocar de escopo invalida a lista carregada: ela é de outra empresa (ou de outro
    // conjunto delas). Sem isto o dropdown seguiria mostrando as opções antigas até que
    // alguém o reabrisse com a lista vazia — e `load` só busca quando `users` está vazio.
    useEffect(() => {
        setUsers([])
    }, [scope, companyId])

    // Resolve o `value` recebido de fora para o registro completo (nome/e-mail).
    // Antes, sem a lista carregada, o campo exibia o placeholder técnico
    // "User #17" — e como a lista só era buscada no foco do input, bastava a
    // tela remontar o seletor com um filtro já aplicado para o operador ver o id
    // cru no lugar do nome. Agora a lista é buscada quando há `value` sem nome.
    useEffect(() => {
        if (!value) {
            setSelected(null)
            return
        }
        const found = users.find((u: any) => u.id === value)
        if (found) setSelected(found)
        else if (users.length === 0 && !loading) load()
    }, [value, users, loading])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        if (showDropdown) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showDropdown])

    /**
     * Quem casa pelo NOME vem antes de quem casa só pelo e-mail — e, dentro do
     * nome, quem começa com o termo vem primeiro.
     *
     * Sem isso o e-mail sequestrava a busca: todo mundo da casa tem e-mail
     * `@mediardex.com`, então procurar o usuário chamado "Mediar" devolvia a
     * empresa inteira em ordem alfabética e o único item certo ficava fora da
     * área visível do dropdown — na prática, "não encontra".
     */
    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase()
        if (!q) return users

        const byName: User[] = []
        const byEmailOnly: User[] = []
        for (const u of users as any[]) {
            if (u.name?.toLowerCase().includes(q)) byName.push(u)
            else if (u.email?.toLowerCase().includes(q)) byEmailOnly.push(u)
        }
        // `sort` é estável: dentro de cada grupo a ordem alfabética da API se mantém.
        byName.sort((a: any, b: any) => {
            const aStarts = a.name?.toLowerCase().startsWith(q) ? 0 : 1
            const bStarts = b.name?.toLowerCase().startsWith(q) ? 0 : 1
            return aStarts - bStarts
        })
        return [...byName, ...byEmailOnly]
    }, [users, searchTerm])

    const handleClear = () => {
        setSelected(null)
        onChange(null, null)
        setSearchTerm('')
    }

    const isSm = size === 'sm'
    /** Há filtro aplicado, mas o nome ainda está sendo buscado (ver o efeito acima). */
    const resolving = !!value && !selected

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            {resolving ? (
                <div
                    className={
                        'flex items-center gap-2 input input-bordered w-full text-base-content/60 ' +
                        (isSm ? 'input-sm!' : '')
                    }
                >
                    <span className="loading loading-spinner loading-xs" />
                    <span className="truncate">Carregando...</span>
                </div>
            ) : selected ? (
                isSm ? (
                    <div className="relative">
                        <div className="flex items-center gap-2 input input-bordered input-sm! w-full pr-9">
                            <MdPerson className="text-base-content/60 flex-shrink-0" />
                            <span className="truncate">{(selected as any).name}</span>
                        </div>
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            onClick={handleClear}
                            disabled={disabled}
                        >
                            <MdClose />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                        <MdPerson className="text-base-content/60" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                                {(selected as any).name}
                            </p>
                            {(selected as any).email && (
                                <p className="text-xs text-base-content/60 truncate">
                                    {(selected as any).email}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-circle"
                            onClick={handleClear}
                            disabled={disabled}
                        >
                            <MdClose />
                        </button>
                    </div>
                )
            ) : (
                <div className="relative">
                    <div className="relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            className={
                                'input input-bordered w-full pl-10 ' +
                                (isSm ? 'input-sm!' : '')
                            }
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={() => {
                                setShowDropdown(true)
                                if (users.length === 0) load()
                            }}
                            disabled={disabled}
                        />
                    </div>
                    {showDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {loading && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                    Carregando...
                                </div>
                            )}
                            {!loading && filtered.length === 0 && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    Nenhum usuário encontrado
                                </div>
                            )}
                            {!loading &&
                                filtered.map((u: any) => (
                                    <button
                                        key={u.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                        onClick={() => {
                                            setSelected(u)
                                            onChange(u.id, u)
                                            setShowDropdown(false)
                                            setSearchTerm('')
                                        }}
                                    >
                                        <span className="font-medium text-sm">
                                            {u.name}
                                        </span>
                                        {u.email && (
                                            <span className="text-xs text-base-content/60 ml-2">
                                                {u.email}
                                            </span>
                                        )}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
